/**
 * @file: OrderDraftManager.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { isOrderDraftEmpty } from '@euler/app/flows/order/functions/makeOrderDraftFromContext';
import {
  asyncStorageSubjectForKey$,
  clearAsyncStorageSubjectForKey,
} from '@euler/functions/useAsyncStorage';
import { comparerDesc, makeDebug } from '@euler/utils';
import produce from 'immer';
import { BehaviorSubject } from 'rxjs';
import { OrderDraft } from '../types';

const debug = makeDebug('order:draft-manager', __DEV__);

export class OrderDraftManager {
  readonly drafts$: BehaviorSubject<OrderDraft[] | null | undefined>;
  readonly key: string;

  constructor(
    readonly orgId: number,
    readonly storeId: number,
  ) {
    this.key = `${orgId}:${storeId}:order-drafts`;
    this.drafts$ =
      orgId && storeId
        ? asyncStorageSubjectForKey$(this.key)
        : new BehaviorSubject<OrderDraft[] | null | undefined>([]);
  }

  getDraft(id: string) {
    return this.drafts$.value?.find(x => x.id === id);
  }

  getLatestDraft(draftIdToExclude?: string): OrderDraft | undefined {
    const list = [...(this.drafts$.value ?? [])]
      .filter(x => draftIdToExclude == null || x.id !== draftIdToExclude)
      .sort((x, y) => comparerDesc(x.timestamp, y.timestamp));
    return list[0];
  }

  clearDraftExcept(id: string) {
    this.drafts$.next(this.drafts$.value?.filter(x => x.id === id));
  }

  saveDraft(orderDraft: OrderDraft) {
    const existing = this.getDraft(orderDraft.id);
    if (
      !existing &&
      (!orderDraft.form.licensePlateNo || isOrderDraftEmpty(orderDraft))
    ) {
      // don't save empty order draft.
      return;
    }

    if (existing && draftContentEquals(existing, orderDraft)) {
      return;
    }

    const drafts = produce(this.drafts$.value ?? [], draft => {
      const index = draft.findIndex(x => x.id === orderDraft.id);
      if (index >= 0) {
        draft[index] = orderDraft;
      } else {
        draft.push(orderDraft);
      }
    });
    this.drafts$.next(drafts);

    debug('draft saved');
  }

  removeDraft(id: string) {
    const drafts = produce(this.drafts$.value ?? [], draft => {
      const index = draft.findIndex(x => x.id === id);
      if (index >= 0) {
        draft.splice(index, 1);
      }
    });
    this.drafts$.next(drafts);
  }

  dispose() {
    debug('order draft manager disposed (key: %s)', this.key);
    clearAsyncStorageSubjectForKey(this.key);
  }
}

function draftContentEquals(a: OrderDraft, b: OrderDraft) {
  const x = JSON.stringify({ ...a, timestamp: undefined });
  const y = JSON.stringify({ ...b, timestamp: undefined });
  return x === y;
}
